import React from "react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Tree, Waves, Cpu, Wind, BatteryFull, ArrowRight, ShareNetwork } from "@phosphor-icons/react";
import { DASHBOARD } from "@/constants/testIds";
import { FoundingBadge } from "@/components/dashboard/FoundingBadge";

const Ring = ({ score = 0 }) => {
    const size = 200;
    const stroke = 12;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(1, score / 10000));
    const offset = c - pct * c;

    return (
        <div className="relative flex items-center justify-center">
            <svg width={size} height={size} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    strokeWidth={stroke}
                    stroke="#1a1a1c"
                    fill="none"
                />
                <motion.circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    strokeWidth={stroke}
                    stroke="url(#eviq-ring)"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={c}
                    initial={{ strokeDashoffset: c }}
                    animate={{ strokeDashoffset: offset }}
                    transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                />
                <defs>
                    <linearGradient id="eviq-ring" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#a3e635" />
                        <stop offset="100%" stopColor="#06b6d4" />
                    </linearGradient>
                </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-mono-metric text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                    Earth Score
                </span>
                <span
                    data-testid={DASHBOARD.heroEarthScore}
                    className="font-mono-metric text-5xl font-bold text-white"
                >
                    <CountUp end={score} duration={2} separator="," />
                </span>
                <span className="mt-1 font-mono-metric text-[10px] text-zinc-500">
                    of 10,000
                </span>
            </div>
        </div>
    );
};

const Metric = ({ testId, icon: Icon, iconColor, label, value, suffix, decimals = 0 }) => (
    <div data-testid={testId} className="flex flex-col gap-2 border-l border-[#1f1f22] pl-4 sm:pl-5">
        <div className="flex items-center gap-2 text-zinc-500">
            <Icon size={14} color={iconColor} weight="fill" />
            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                {label}
            </span>
        </div>
        <div className="font-mono-metric text-2xl font-bold text-white sm:text-3xl">
            <CountUp end={value} duration={2.2} decimals={decimals} separator="," />
            {suffix && (
                <span className="ml-1 text-sm font-normal text-zinc-500 sm:text-base">
                    {suffix}
                </span>
            )}
        </div>
    </div>
);

export const HeroImpact = ({ user }) => {
    if (!user) return null;
    return (
        <section
            data-testid={DASHBOARD.hero}
            className="eviq-grain eviq-grid-lines relative overflow-hidden rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-12"
        >
            <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#a3e635]/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-[#06b6d4]/10 blur-3xl" />

            <div className="relative flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <span className="eviq-chip">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#a3e635]" />
                        Your impact
                    </span>
                    <FoundingBadge
                        tier={user.founder_tier}
                        memberNumber={user.member_number}
                        size="sm"
                        testId="hero-founding-badge"
                    />
                </div>
                <h1 className="font-editorial text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                    Every mile with EVIQ
                    <br />
                    <span className="italic text-zinc-400">is a mile toward restoration.</span>
                </h1>
                <p className="max-w-xl text-sm text-zinc-500 lg:text-base">
                    Welcome back, {user.display_name}. Your membership is quietly restoring forests,
                    pulling plastic from the ocean, and keeping batteries in service longer.
                </p>
            </div>

            <div className="relative mt-8 grid grid-cols-1 gap-8 sm:mt-10 sm:gap-10 lg:grid-cols-[200px_1fr] lg:items-center">
                <Ring score={user.earth_score} />

                <div className="grid grid-cols-2 gap-5 sm:gap-6 lg:grid-cols-4">
                    <Metric
                        testId={DASHBOARD.heroBatteryHealth}
                        icon={BatteryFull}
                        iconColor="#a3e635"
                        label="Battery Health"
                        value={user.battery_health}
                        suffix="%"
                    />
                    <Metric
                        testId={DASHBOARD.heroTrees}
                        icon={Tree}
                        iconColor="#a3e635"
                        label="Trees Restored"
                        value={user.trees_restored}
                    />
                    <Metric
                        testId={DASHBOARD.heroOcean}
                        icon={Waves}
                        iconColor="#06b6d4"
                        label="Ocean Plastic"
                        value={user.ocean_plastic_lbs}
                        suffix="lbs"
                    />
                    <Metric
                        testId={DASHBOARD.heroCo2}
                        icon={Wind}
                        iconColor="#fbbf24"
                        label="CO₂ Prevented"
                        value={user.co2_saved_lbs}
                        suffix="lbs"
                    />
                    <Metric
                        icon={Cpu}
                        iconColor="#8b5cf6"
                        label="Batteries Protected"
                        value={user.batteries_protected}
                    />
                    <Metric
                        testId={DASHBOARD.heroBatteryLife}
                        icon={BatteryFull}
                        iconColor="#a3e635"
                        label="Battery Life Extended"
                        value={user.battery_life_extended_years}
                        suffix="yrs"
                        decimals={1}
                    />
                    <div className="col-span-2 flex flex-col justify-center border-l border-[#1f1f22] pl-5">
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                            Member since
                        </span>
                        <span className="mt-2 font-editorial text-xl italic text-zinc-200">
                            {new Date(user.member_since).toLocaleDateString(undefined, {
                                month: "long",
                                year: "numeric",
                            })}
                        </span>
                        <span className="mt-1 text-xs text-zinc-500">
                            {user.referrals} friends invited · {user.plan} plan
                        </span>
                    </div>
                </div>
            </div>

            {/* CTA · Deep-link into the user's personal impact profile */}
            <div className="relative mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[#1f1f22] pt-6">
                <div>
                    <p className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                        Your public impact page
                    </p>
                    <p className="mt-1 font-editorial text-lg italic text-zinc-200">
                        /impact/founder-{String(user.member_number).padStart(4, "0")}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link
                        to={`/impact/founder-${String(user.member_number).padStart(4, "0")}`}
                        data-testid="hero-view-my-impact"
                        className="inline-flex items-center gap-2 rounded-full bg-[#a3e635] px-5 py-2.5 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                    >
                        View my impact page
                        <ArrowRight size={14} weight="bold" />
                    </Link>
                    <Link
                        to={`/impact/founder-${String(user.member_number).padStart(4, "0")}`}
                        className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-4 py-2.5 text-sm text-zinc-200 transition-colors hover:border-[#3a3a3f]"
                    >
                        <ShareNetwork size={14} weight="bold" />
                        Share
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default HeroImpact;
